const MOSCOW_TZ = 'Europe/Moscow';

// Текущий год в московской таймзоне — нужен, чтобы решить, показывать ли год
// в дате события (см. defaultEvent).
const getCurrentYear = (): string =>
  new Intl.DateTimeFormat('ru-RU', {
    year: 'numeric',
    timeZone: MOSCOW_TZ,
  }).format(new Date());

// Длительность вебинара по умолчанию, когда дата окончания неизвестна (40 минут).
// Тот же запас, что и в баннере детальной страницы (widgets/promo/Promo.vue).
const DEFAULT_WEBINAR_DURATION_MS = 40 * 60 * 1000;

const toTimestamp = (value: number | string): number => {
  const num =
    typeof value === 'string' ? Number(value) || new Date(value).getTime() : value;
  return !num || num <= 0 || isNaN(num) ? 0 : num;
};

type DateFormatParts = {
  /** День без ведущего нуля: «9», а не «09». */
  day: string;
  month: string;
  shortMonth: string;
  year: string;
  weekday: string;
  hour: string;
  minute: string;
  /** Дата без времени: «9 июня 2026». */
  default: string;
  /** Дата события: «26 июня (пт), 15:00 мск» (год — только если не текущий). */
  defaultEvent: string;
};

const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const RU_DATE_RE = /^(\d{2})\.(\d{2})\.(\d{4})(?:\s+\d{2}:\d{2}(?::\d{2})?)?$/;

/** Календарная дата без значимого для вывода времени. */
type CalendarDate = { year: number; month: number; day: number };

/**
 * Календарные даты приходят с бэкенда в трёх видах: `2021-04-09` (`Y-m-d`, ряды
 * котировок фондов), `09.04.2021` (`d.m.Y`, структура портфеля) и
 * `09.04.2021 17:31:00` (дата публикации из Битрикса). Разбираем строку сами:
 * локальная таймзона Node.js или браузера не должна сдвигать календарный день.
 */
const parseCalendarDate = (value: string): CalendarDate | null => {
  const iso = ISO_DATE_RE.exec(value);
  if (iso) return { year: Number(iso[1]), month: Number(iso[2]), day: Number(iso[3]) };

  const ru = RU_DATE_RE.exec(value);
  if (ru) return { year: Number(ru[3]), month: Number(ru[2]), day: Number(ru[1]) };

  return null;
};

/**
 * Формат календарной даты: день, месяц и год берём прямо из строки.
 *
 * Времени в такой дате нет, поэтому и не выдумываем его: `hour`, `minute` и
 * `defaultEvent` в ответе отсутствуют — событие без времени показать нельзя.
 * Название месяца и день недели спрашиваем у Intl в UTC: дату собираем в UTC,
 * читаем в UTC, так что сутки не сдвинутся ни на сервере, ни в браузере.
 */
const formatDateOnly = ({
  year,
  month,
  day,
}: CalendarDate): Partial<DateFormatParts> => {
  const date = new Date(Date.UTC(year, month - 1, day));

  // `Date.UTC` молча заворачивает переполнение (`2021-02-31` → 3 марта) —
  // сверяем, что дата не «съехала», иначе это мусор во входной строке.
  const isReal =
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day;

  if (!isReal) return {};

  // Месяц просим вместе с днём и годом: в одиночку `month: 'long'` даёт
  // именительный падеж («август»), а в дате нужен родительный («августа»).
  const monthName = new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
    .formatToParts(date)
    .find((part) => part.type === 'month').value;

  const weekday = new Intl.DateTimeFormat('ru-RU', {
    weekday: 'short',
    timeZone: 'UTC',
  }).format(date);

  return {
    day: String(day),
    month: monthName,
    shortMonth: String(month).padStart(2, '0'),
    year: String(year),
    weekday,
    default: `${day} ${monthName} ${year}`,
  };
};

/**
 * Форматирование даты в единой таймзоне (Москва) для совпадения SSR и клиента (гидрация).
 * Раньше day/shortMonth брались из getDate()/getMonth() (локальное время), из-за чего
 * на сервере и в браузере получались разные даты и возникал hydration mismatch.
 *
 * Календарную дату без времени (`2021-04-09`, `09.04.2021`) отдаём через
 * `formatDateOnly`. Битриксовую дату публикации `09.04.2021 17:31:00` форматируем
 * так же: время для неё не выводится.
 *
 * При невалидной дате возвращает пустой объект — проверяйте наличие полей.
 */
export const useDateFormat = (timestamp: number | string): Partial<DateFormatParts> => {
  if (typeof timestamp === 'string') {
    const calendarDate = parseCalendarDate(timestamp.trim());
    if (calendarDate) return formatDateOnly(calendarDate);
  }

  const num = toTimestamp(timestamp);
  if (!num) return {};

  const date = new Date(num);
  if (isNaN(date.getTime())) return {};

  const parts = new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: MOSCOW_TZ,
  }).formatToParts(date);

  const numericMonthParts = new Intl.DateTimeFormat('ru-RU', {
    month: '2-digit',
    timeZone: MOSCOW_TZ,
  }).formatToParts(date);

  const weekdayParts = new Intl.DateTimeFormat('ru-RU', {
    weekday: 'short',
    timeZone: MOSCOW_TZ,
  }).formatToParts(date);

  const minute = parts.find((part) => part.type === 'minute').value;
  const hour = parts.find((part) => part.type === 'hour').value;
  const day = parts.find((part) => part.type === 'day').value;
  const month = parts.find((part) => part.type === 'month').value;
  const year = parts.find((part) => part.type === 'year').value;
  const shortMonth = numericMonthParts.find((part) => part.type === 'month').value;
  const weekday = weekdayParts.find((part) => part.type === 'weekday').value;

  // Год в дате события показываем только когда он отличается от текущего:
  // вебинар на январь, заведённый в декабре, получит «15 января 2027 (чт), 15:00 мск»,
  // а событие текущего года — «26 июня (пт), 15:00 мск».
  const eventYear = year === getCurrentYear() ? '' : ` ${year}`;

  return {
    day,
    month,
    shortMonth,
    year,
    weekday,
    hour,
    minute,
    default: `${day} ${month} ${year}`,
    defaultEvent: `${day} ${month}${eventYear} (${weekday}), <span>${hour}:${minute} мск</span>`,
  };
};

/**
 * Текст даты публикации для карточек, слайдеров и списков.
 *
 * Вебинар до эфира и во время него — формат события «26 июня (пт), 15:00 мск»,
 * прошедший вебинар и любая другая публикация — только дата «13 июня 2026»
 * (так же, как в баннере на детальной странице вебинара).
 *
 * @param timestamp Дата начала (для вебинара — DATE_START).
 * @param options.isWebinar Публикация является вебинаром.
 * @param options.dateEnd Дата окончания вебинара; если её нет, считаем эфир
 *   завершённым через DEFAULT_WEBINAR_DURATION_MS после старта.
 */
export const usePublicationDateText = (
  timestamp: number | string,
  options: { isWebinar?: boolean; dateEnd?: number | string } = {},
): string => {
  const { default: dateOnly, defaultEvent } = useDateFormat(timestamp);
  if (!dateOnly) return '';
  // Без времени формат события не собрать (календарная дата) — показываем дату.
  if (!options.isWebinar || !defaultEvent) return dateOnly;

  const start = toTimestamp(timestamp);
  const end = toTimestamp(options.dateEnd ?? 0) || start + DEFAULT_WEBINAR_DURATION_MS;

  return Date.now() > end ? dateOnly : defaultEvent;
};
